import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  HStack,
  Heading,
  Image,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  BookOpen,
  CalendarDays,
  Clock3,
  Download,
  FileDown,
  FolderOpen,
  History,
  Search,
  Send,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  setHelpGuideSearch,
  toggleHelpGuideBookmark,
  viewHelpGuide,
} from "../store/actions/helpGuideActions";
import guidesData from "../data/helpGuides.json";
import { downloadHelpGuidePdf } from "../helpers/helpGuidePdfExport";
import {
  getGuidesByIds,
  groupGuidesByCategory,
  searchHelpGuides,
  sortGuidesByVisitCount,
  sortGuidesForLanding,
} from "../helpers/helpGuideSearch";

const quickSearches = ["export", "subscription", "past reports", "date range", "branch", "ticket data"];

const taskIcons = {
  "create-report-subscription": Send,
  "save-report-download-past": History,
  "export-report-pdf-csv": FileDown,
  "choose-report-range-date": CalendarDays,
  "export-ticket-data": Download,
  "select-organization-branch": SlidersHorizontal,
};

const GuideMeta = ({ guide, visitCount }) => (
  <HStack gap="6px" mt="10px" flexWrap="wrap">
    <Box px="7px" py="3px" borderRadius="4px" bg="#f8f9fa" fontSize="11px">
      {guide.category}
    </Box>
    <Box px="7px" py="3px" borderRadius="4px" bg="#f8f9fa" fontSize="11px">
      {guide.steps.length} steps
    </Box>
    <Box px="7px" py="3px" borderRadius="4px" bg="#f8f9fa" fontSize="11px">
      {guide.screenshots.length} screenshots
    </Box>
    {visitCount > 0 ? (
      <Text fontSize="11px" color="var(--lightGrey)">
        Viewed {visitCount}x
      </Text>
    ) : null}
  </HStack>
);

const BookmarkButton = ({ isBookmarked, onBookmark, size = "xs" }) => (
  <Button
    size={size}
    variant="ghost"
    aria-label={isBookmarked ? "Remove bookmark" : "Bookmark guide"}
    onClick={(event) => {
      event.stopPropagation();
      onBookmark();
    }}
  >
    {isBookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
  </Button>
);

const GuideCard = ({
  guide,
  isBookmarked,
  visitCount,
  onSelect,
  onBookmark,
  variant = "default",
}) => {
  const Icon = taskIcons[guide.id] || BookOpen;
  const isLatest = variant === "latest";
  const summary = isLatest ? guide.latestSummary || guide.summary : guide.summary;
  const handleOpenKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor="var(--borderGrey)"
      borderRadius="8px"
      p="14px"
      bg="white"
      minH={isLatest ? "188px" : "154px"}
      w="100%"
      transition="border-color 160ms ease, box-shadow 160ms ease"
      _hover={{ borderColor: "var(--blue)", boxShadow: "0 8px 22px rgba(17, 24, 39, 0.08)" }}
    >
      <Grid templateColumns="minmax(0, 1fr) auto" gap="10px" alignItems="start">
        <Box
          role="button"
          tabIndex={0}
          textAlign="left"
          cursor="pointer"
          minW={0}
          _focusVisible={{ outline: "2px solid var(--blue)", outlineOffset: "2px" }}
          onClick={onSelect}
          onKeyDown={handleOpenKeyDown}
        >
          <HStack align="flex-start" gap="10px" minW={0}>
            <Box
              w="34px"
              h="34px"
              borderRadius="8px"
              bg={isLatest ? "#eef4ff" : "#f8f9fa"}
              color="var(--blue)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon size={18} />
            </Box>
            <Box minW={0}>
              <HStack gap="6px" mb="5px" flexWrap="wrap">
                {isLatest ? (
                  <Box px="7px" py="3px" borderRadius="4px" bg="#111827" color="white" fontSize="11px">
                    {guide.latestLabel || "Updated"}
                  </Box>
                ) : null}
                <Text fontSize="12px" color="var(--gray)" fontWeight="700">
                  {guide.category}
                </Text>
              </HStack>
              <Heading as="h3" fontSize="16px" color="var(--black-dry)" lineHeight="1.25">
                {guide.title}
              </Heading>
            </Box>
          </HStack>
          <Text fontSize="13px" color="var(--gray)" lineHeight="1.5" mt="10px">
            {summary}
          </Text>
          <GuideMeta guide={guide} visitCount={visitCount} />
        </Box>
        <BookmarkButton isBookmarked={isBookmarked} onBookmark={onBookmark} />
      </Grid>
    </Box>
  );
};

const SectionHeader = ({ title, description, icon: Icon = FolderOpen }) => (
  <HStack justify="space-between" align="flex-end" gap="12px" mb="10px">
    <Box>
      <HStack gap="7px" mb="3px">
        <Icon size={17} color="var(--blue)" />
        <Heading as="h2" fontSize="18px" color="var(--black-dry)">
          {title}
        </Heading>
      </HStack>
      {description ? (
        <Text fontSize="13px" color="var(--gray)">
          {description}
        </Text>
      ) : null}
    </Box>
  </HStack>
);

const GuideRow = ({ title, description, icon, guides, bookmarks, visitCounts, onSelect, onBookmark, variant }) => {
  if (!guides.length) return null;

  return (
    <Box mb="24px">
      <SectionHeader title={title} description={description} icon={icon} />
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }} gap="12px">
        {guides.map((guide) => (
          <GuideCard
            key={guide.id}
            guide={guide}
            variant={variant}
            isBookmarked={bookmarks.includes(guide.id)}
            visitCount={visitCounts[guide.id] || 0}
            onSelect={() => onSelect(guide.id)}
            onBookmark={() => onBookmark(guide.id)}
          />
        ))}
      </Grid>
    </Box>
  );
};

const CategoryGuideItem = ({ guide, isBookmarked, onSelect, onBookmark }) => (
  <HStack
    justify="space-between"
    align="center"
    border="1px solid"
    borderColor="#f1f2f4"
    borderRadius="6px"
    p="10px"
    bg="#fbfbfc"
    _hover={{ borderColor: "var(--blue)", bg: "#f8f9ff" }}
  >
    <Box
      role="button"
      tabIndex={0}
      textAlign="left"
      cursor="pointer"
      minW={0}
      flex="1"
      _focusVisible={{ outline: "2px solid var(--blue)", outlineOffset: "2px" }}
      onClick={() => onSelect(guide.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(guide.id);
        }
      }}
    >
      <Text fontSize="14px" color="var(--black-dry)" fontWeight="700">
        {guide.title}
      </Text>
      <Text fontSize="12px" color="var(--gray)">
        {guide.steps.length} steps
      </Text>
    </Box>
    <BookmarkButton
      isBookmarked={isBookmarked}
      onBookmark={() => onBookmark(guide.id)}
    />
  </HStack>
);

const CategoryBrowser = ({ categories, bookmarks, visitCounts, onSelect, onBookmark }) => (
  <Box mb="24px">
    <SectionHeader
      title="Browse by category"
      description="Use categories when you know the Queue Management Reports area but not the exact guide name."
      icon={FolderOpen}
    />
    <Grid templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))" }} gap="12px">
      {Object.entries(categories).map(([category, guides]) => (
        <Box key={category} border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" bg="white" p="14px">
          <HStack justify="space-between" align="center" mb="10px">
            <Heading as="h3" fontSize="16px" color="var(--black-dry)">
              {category}
            </Heading>
            <Text fontSize="12px" color="var(--gray)">
              {guides.length} guides
            </Text>
          </HStack>
          <VStack gap="8px" align="stretch">
            {sortGuidesForLanding(guides).map((guide) => (
              <CategoryGuideItem
                key={guide.id}
                guide={guide}
                isBookmarked={bookmarks.includes(guide.id)}
                onSelect={onSelect}
                onBookmark={onBookmark}
              />
            ))}
          </VStack>
        </Box>
      ))}
    </Grid>
  </Box>
);

const QuickSearches = ({ onSearch }) => (
  <HStack gap="8px" flexWrap="wrap" mt="12px">
    {quickSearches.map((query) => (
      <Button
        key={query}
        size="sm"
        variant="outline"
        borderRadius="6px"
        onClick={() => onSearch(query)}
      >
        {query}
      </Button>
    ))}
  </HStack>
);

const HelpSearch = ({ searchQuery, onSearchChange, onQuickSearch }) => (
  <Box border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" bg="white" p="14px" mb="20px">
    <HStack
      border="1px solid"
      borderColor="var(--borderGrey)"
      borderRadius="8px"
      bg="#fbfbfc"
      px="12px"
      minH="48px"
    >
      <Search size={18} color="var(--gray)" />
      <Input
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search export, subscription, past reports..."
        border="0"
        outline="0"
        aria-label="Search help guides"
        fontSize="15px"
      />
    </HStack>
    <QuickSearches onSearch={onQuickSearch} />
  </Box>
);

const HelpActions = ({ isDownloading, onDownload }) => (
  <Box
    border="1px solid"
    borderColor="var(--borderGrey)"
    borderRadius="8px"
    bg="white"
    p="10px"
    minW={{ base: "100%", sm: "236px" }}
  >
    <Text fontSize="12px" color="var(--gray)" fontWeight="700" mb="7px">
      Help actions
    </Text>
    <Button
      size="sm"
      w="100%"
      justifyContent="flex-start"
      gap="8px"
      loading={isDownloading}
      loadingText="Preparing PDF..."
      disabled={isDownloading}
      aria-label="Download full help guide"
      onClick={onDownload}
    >
      <FileDown size={16} />
      {isDownloading ? "Preparing PDF..." : "Download full help guide"}
    </Button>
  </Box>
);

const HelpHub = ({
  latestGuides,
  popularGuides,
  bookmarkedGuides,
  recentGuides,
  mostVisitedGuides,
  categories,
  bookmarks,
  visitCounts,
  onSelect,
  onBookmark,
  onQuickSearch,
}) => (
  <Box>
    <GuideRow
      title="Latest"
      description="New and recently updated Queue Management Reports workflows, with the shortest path to using them."
      icon={Clock3}
      guides={latestGuides}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
      variant="latest"
    />
    <GuideRow
      title="Popular tasks"
      description="The workflows admins usually need first."
      icon={Star}
      guides={popularGuides}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
    />
    <GuideRow
      title="Bookmarked"
      description="Guides you marked for quick access."
      icon={BookmarkCheck}
      guides={bookmarkedGuides}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
    />
    <GuideRow
      title="Recently viewed"
      description="Pick up where you left off."
      icon={History}
      guides={recentGuides}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
    />
    <GuideRow
      title="Most visited"
      description="Your most used help guides on this browser."
      icon={BookOpen}
      guides={mostVisitedGuides}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
    />
    <CategoryBrowser
      categories={categories}
      bookmarks={bookmarks}
      visitCounts={visitCounts}
      onSelect={onSelect}
      onBookmark={onBookmark}
    />
    <Box border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" bg="white" p="14px">
      <Text fontWeight="700" color="var(--black-dry)" mb="5px">
        Looking for something specific?
      </Text>
      <Text fontSize="13px" color="var(--gray)" mb="10px">
        Start with a common Queue Management Reports term and narrow from the search results.
      </Text>
      <QuickSearches onSearch={onQuickSearch} />
    </Box>
  </Box>
);

const SearchResults = ({ searchResults, bookmarks, visitCounts, onSelect, onBookmark, onQuickSearch }) => (
  <Box>
    <SectionHeader
      title={`Search results (${searchResults.length})`}
      description="Results match guide titles, summaries, tags, keywords, and step text."
      icon={Search}
    />
    {searchResults.length ? (
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }} gap="12px">
        {searchResults.map((guide) => (
          <GuideCard
            key={guide.id}
            guide={guide}
            isBookmarked={bookmarks.includes(guide.id)}
            visitCount={visitCounts[guide.id] || 0}
            onSelect={() => onSelect(guide.id)}
            onBookmark={() => onBookmark(guide.id)}
          />
        ))}
      </Grid>
    ) : (
      <Box border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" bg="white" p="18px">
        <BookOpen size={22} />
        <Text mt="8px" fontWeight="700">
          No guides found
        </Text>
        <Text fontSize="13px" color="var(--gray)" mb="10px">
          Try a broader term like export, subscription, past reports, date range, branch, or ticket data.
        </Text>
        <QuickSearches onSearch={onQuickSearch} />
      </Box>
    )}
  </Box>
);

const GuideScreenshot = ({ screenshot, mt = "10px", mb = "0" }) => {
  if (!screenshot) return null;

  return (
    <Box mt={mt} mb={mb} w="100%" display="flex" justifyContent="flex-start">
      <Image
        src={screenshot.src}
        alt={screenshot.alt}
        border="1px solid"
        borderColor="var(--borderGrey)"
        borderRadius="8px"
        w={{ base: "100%", md: "76%", xl: "68%" }}
        maxW="880px"
        bg="#f8f9fa"
      />
    </Box>
  );
};

const GuideDetail = ({ guide, relatedGuides, isBookmarked, onBookmark, onSelectRelated, onBack }) => (
  <Box bg="white" border="1px solid" borderColor="var(--borderGrey)" borderRadius="8px" p="20px">
    <Button size="sm" variant="ghost" mb="14px" onClick={onBack}>
      <ArrowLeft size={16} />
      Back to Help
    </Button>
    <HStack justify="space-between" align="flex-start" gap="14px" mb="12px">
      <Box>
        <Text fontSize="12px" color="var(--gray)" fontWeight="700" mb="5px">
          {guide.category}
        </Text>
        <Heading as="h2" fontSize="24px" color="var(--black-dry)">
          {guide.title}
        </Heading>
      </Box>
      <Button size="sm" variant="outline" onClick={onBookmark}>
        {isBookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
        {isBookmarked ? "Bookmarked" : "Bookmark"}
      </Button>
    </HStack>

    <Text color="var(--gray)" lineHeight="1.6" mb="16px">
      {guide.summary}
    </Text>

    {guide.steps.some((step) => step.screenshot)
      ? null
      : guide.screenshots.map((screenshot) => (
          <GuideScreenshot key={screenshot.src} screenshot={screenshot} mt="0" mb="18px" />
        ))}

    <VStack gap="12px" align="stretch">
      {guide.steps.map((step, index) => (
        <HStack key={step.title} align="flex-start" gap="12px">
          <Box
            w="28px"
            h="28px"
            borderRadius="999px"
            bg="var(--blue)"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="13px"
            fontWeight="700"
            flexShrink={0}
          >
            {index + 1}
          </Box>
          <Box w="100%" minW={0}>
            <Text fontWeight="700" color="var(--black-dry)" mb="3px">
              {step.title}
            </Text>
            <Text fontSize="14px" color="var(--gray)" lineHeight="1.6">
              {step.body}
            </Text>
            <GuideScreenshot screenshot={step.screenshot} />
          </Box>
        </HStack>
      ))}
    </VStack>

    {relatedGuides.length ? (
      <Box mt="20px" pt="16px" borderTop="1px solid" borderColor="var(--borderGrey)">
        <Text fontSize="13px" fontWeight="700" color="var(--gray-45)" mb="9px">
          Related guides
        </Text>
        <HStack gap="8px" flexWrap="wrap">
          {relatedGuides.map((relatedGuide) => (
            <Button
              key={relatedGuide.id}
              size="sm"
              variant="outline"
              onClick={() => onSelectRelated(relatedGuide.id)}
            >
              {relatedGuide.title}
            </Button>
          ))}
        </HStack>
      </Box>
    ) : null}
  </Box>
);

function HelpGuide() {
  const dispatch = useDispatch();
  const helpGuideState = useSelector((state) => state.helpGuideReducer || {});
  const {
    bookmarks = [],
    recentGuideIds = [],
    visitCounts = {},
    searchQuery = "",
  } = helpGuideState;

  const guides = guidesData.guides;
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [isDownloadingHelpPdf, setIsDownloadingHelpPdf] = useState(false);

  const selectedGuide = guides.find((guide) => guide.id === selectedGuideId);
  const searchResults = useMemo(() => searchHelpGuides(guides, searchQuery), [guides, searchQuery]);
  const isSearching = searchQuery.trim().length > 0;
  const bookmarkedGuides = getGuidesByIds(guides, bookmarks);
  const recentGuides = getGuidesByIds(guides, recentGuideIds);
  const latestGuides = sortGuidesForLanding(guides.filter((guide) => guide.isLatest));
  const popularGuides = sortGuidesForLanding(guides.filter((guide) => guide.common));
  const mostVisitedGuides = sortGuidesByVisitCount(guides, visitCounts)
    .filter((guide) => visitCounts[guide.id] > 0)
    .slice(0, 4);
  const categories = groupGuidesByCategory(guides);
  const relatedGuides = getGuidesByIds(guides, selectedGuide?.relatedGuides || []);

  const selectGuide = (guideId) => {
    setSelectedGuideId(guideId);
    dispatch(viewHelpGuide(guideId));
  };

  const toggleBookmark = (guideId) => {
    dispatch(toggleHelpGuideBookmark(guideId));
  };

  const updateSearch = (query) => {
    setSelectedGuideId(null);
    dispatch(setHelpGuideSearch(query));
  };

  const returnToHub = () => {
    setSelectedGuideId(null);
    dispatch(setHelpGuideSearch(""));
  };

  const downloadFullHelpGuide = async () => {
    setIsDownloadingHelpPdf(true);

    try {
      await downloadHelpGuidePdf(guides);
    } catch (error) {
      console.error("Error downloading help guide PDF:", error);
      toast.error("Help guide download failed. Please try again.");
    } finally {
      setIsDownloadingHelpPdf(false);
    }
  };

  return (
    <Box w="100%" maxW="1280px">
      <HStack justify="space-between" align="flex-start" gap="16px" mb="16px" flexWrap="wrap">
        <Box>
          <Heading size="lg" color="var(--black-dry)">
            Help Guide
          </Heading>
          <Text color="var(--gray)" mt="5px">
            {guidesData.description}
          </Text>
        </Box>
        <HelpActions
          isDownloading={isDownloadingHelpPdf}
          onDownload={downloadFullHelpGuide}
        />
      </HStack>

      {!selectedGuide ? (
        <HelpSearch
          searchQuery={searchQuery}
          onSearchChange={updateSearch}
          onQuickSearch={updateSearch}
        />
      ) : null}

      {selectedGuide ? (
        <GuideDetail
          guide={selectedGuide}
          relatedGuides={relatedGuides}
          isBookmarked={bookmarks.includes(selectedGuide.id)}
          onBookmark={() => toggleBookmark(selectedGuide.id)}
          onSelectRelated={selectGuide}
          onBack={returnToHub}
        />
      ) : isSearching ? (
        <SearchResults
          searchResults={searchResults}
          bookmarks={bookmarks}
          visitCounts={visitCounts}
          onSelect={selectGuide}
          onBookmark={toggleBookmark}
          onQuickSearch={updateSearch}
        />
      ) : (
        <HelpHub
          latestGuides={latestGuides}
          popularGuides={popularGuides}
          bookmarkedGuides={bookmarkedGuides}
          recentGuides={recentGuides}
          mostVisitedGuides={mostVisitedGuides}
          categories={categories}
          bookmarks={bookmarks}
          visitCounts={visitCounts}
          onSelect={selectGuide}
          onBookmark={toggleBookmark}
          onQuickSearch={updateSearch}
        />
      )}
    </Box>
  );
}

export default HelpGuide;
